import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import PartnerSidebar, { PartnerMobileHeader } from '../../components/Partner/PartnerSidebar';
import PartnerKycForm from '../../components/Partner/PartnerKycForm';
import ThemeToggle from '../../components/UI/ThemeToggle';
import { useChannelPartnerAuth } from '../../contexts/ChannelPartnerAuthContext';
import { HiOutlineShieldCheck, HiOutlineExclamationCircle } from 'react-icons/hi';

const PartnerLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { partner, refreshProfile } = useChannelPartnerAuth();

  if (!partner) return null;

  // Self-signup partners start 'not_submitted' — block the whole portal
  // behind a modal until KYC is filled in (this file) and then approved by a
  // superadmin (see routes/superAdminRoutes.js PATCH /channel-partners/:id/kyc).
  // Superadmin-created partners default to 'not_required' and are never gated.
  const kycBlocked = ['not_submitted', 'pending', 'rejected'].includes(partner.kycStatus);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50/30">
      {/* Dark / light mode toggle — fixed bottom-right, same as the admin panel */}
      <div className="hidden lg:block fixed bottom-5 right-5 z-40">
        <ThemeToggle />
      </div>

      <div className={kycBlocked ? 'pointer-events-none blur-sm select-none' : ''}>
        {/* Mobile Header */}
        <PartnerMobileHeader onMenuClick={() => setSidebarOpen(true)} />

        <div className="flex">
          <PartnerSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

          <main className="flex-1 min-h-screen min-w-0 lg:ml-64">
            <div className="pt-16 lg:pt-0 w-full h-full overflow-x-hidden">
              <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6 sm:py-8">
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>

      {kycBlocked && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-secondary-900/50 backdrop-blur-sm">
          <div className="flex min-h-full items-center justify-center p-4 py-10">
            <div
              className={`w-full rounded-2xl bg-white shadow-2xl ${
                partner.kycStatus === 'not_submitted' ? 'max-w-xl p-6 sm:p-8' : 'max-w-md p-6 sm:p-8 text-center'
              }`}
            >
              {partner.kycStatus === 'not_submitted' ? (
                <PartnerKycForm onSubmitted={refreshProfile} />
              ) : partner.kycStatus === 'rejected' ? (
                <>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-danger-50">
                    <HiOutlineExclamationCircle className="h-7 w-7 text-danger-600" />
                  </div>
                  <h2 className="mt-5 text-xl font-bold text-secondary-900">KYC Application Rejected</h2>
                  <p className="mt-2 text-sm text-secondary-500">
                    {partner.kycRejectionReason || "We weren't able to verify your KYC documents."}
                  </p>
                  <p className="mt-4 text-sm text-secondary-500">
                    Please reply with corrected documents to{' '}
                    <a href="mailto:support@televalley.in" className="font-semibold text-primary-600">support@televalley.in</a>.
                  </p>
                </>
              ) : (
                <>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-warning-50">
                    <HiOutlineShieldCheck className="h-7 w-7 text-warning-600" />
                  </div>
                  <h2 className="mt-5 text-xl font-bold text-secondary-900">KYC Verification In Progress</h2>
                  <p className="mt-2 text-sm text-secondary-500">
                    Please wait while we verify your KYC documents — this usually takes 1–2 business days.
                    We'll email you the moment it's approved, and your dashboard will unlock automatically.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerLayout;
